import {api} from '@/shared/services/api-client';
import {
  fetchAllSchedules,
  fetchSchedulesPage,
  type ScheduleListParams,
} from '@/shared/services/scheduleRetrieval';

/** @deprecated Use `ScheduleListParams` from shared/services/scheduleRetrieval. */
export type SchedulesListParams = ScheduleListParams;

export const schedulesApi = {
  listModerators: () => api.moderators.list(),
  listActiveModerators: () => api.moderators.getActive(),
  getModeratorByEmail: (email: string) => api.moderators.getByEmail(email),
  getModeratorsByIds: (ids: string[]) => api.moderators.getByIds(ids),
  updateModerator: (id: string, data: any) => api.moderators.update(id, data),
  registerModerator: (data: any) => api.moderators.register(data),
  deleteModeratorPermanent: (id: string) => api.moderators.deletePermanent(id),

  listSchedules: (params?: ScheduleListParams) =>
    fetchSchedulesPage(params, 'schedulesApi.listSchedules'),
  /**
   * Fetch every schedule matching `params` by paging through the backend.
   * Use for export / full-set computations instead of a single large `limit`.
   */
  listAll: (params?: ScheduleListParams, pageSize?: number) =>
    fetchAllSchedules(params, pageSize),
  getSchedule: (id: string) => api.schedules.get(id),
  getSchedulesByParticipant: (participantId: string) =>
    api.schedules.getByParticipant(participantId),
  /**
   * Bulk variant — one request for many participants instead of one request
   * per participant. Returns `{ schedulesByParticipant: { [id]: Schedule[] } }`.
   */
  getSchedulesByParticipants: (participantIds: string[], projectId?: string) =>
    api.schedules.getByParticipants(participantIds, projectId),
  createSchedule: (data: any) => api.schedules.create(data),
  createGroupSchedules: (data: any) => api.schedules.createGroup(data),
  createSimpleSchedule: (data: any) => api.schedules.createSimple(data),
  updateSchedule: (id: string, data: any) => api.schedules.update(id, data),
  disqualifySchedule: (id: string, data: { reason: string }) =>
    api.schedules.disqualify(id, data),
  doNotHireSchedule: (id: string, data: { reason: string }) =>
    api.schedules.doNotHire(id, data),
  overbookSchedule: (id: string, data: { reason: string }) =>
    api.schedules.overbook(id, data),
  deleteSchedule: (id: string) => api.schedules.delete(id),

  getAvailableModerators: (projectId: string) =>
    api.schedules.getAvailableModerators(projectId),
  getAvailableSlots: (moderatorId: string, projectId: string, params?: any) =>
    api.schedules.getAvailableSlots(moderatorId, projectId, params),
  // Per-slot capacity: per-slot fill counts + capacity for a session type/date
  getSlotCapacity: (params: {session_type_id: string; date?: string; from?: string; to?: string}) =>
    api.schedules.getSlotCapacity(params),
  participantBook: (data: any) => api.schedules.participantBook(data),
  participantCancel: (scheduleId: string, data: any) =>
    api.schedules.participantCancel(scheduleId, data),
  participantReschedule: (scheduleId: string, data: any) =>
    api.schedules.participantReschedule(scheduleId, data),
  validateParticipantActionLink: (params: {schedule_id: string; action_token: string; action?: 'reschedule' | 'cancel'}) =>
    api.schedules.participantActionValidate(params),

  generateCheckInCode: (scheduleId: string) =>
    api.schedules.generateCheckInCode(scheduleId),
  verifyCheckInCode: (scheduleId: string, data: any) =>
    api.schedules.verifyCheckInCode(scheduleId, data),
  getCheckInStatus: (scheduleId: string) => api.schedules.getCheckInStatus(scheduleId),
  getParticipantMeetings: (participantId: string, params?: any) =>
    api.schedules.getParticipantMeetings(participantId, params),
  addScheduleNote: (
    scheduleId: string,
    data: {body: string; authorName?: string; type?: string},
  ) => api.schedules.addNote(scheduleId, data),

  listModeratorAvailability: (params?: {approved_only?: boolean; status?: string}) =>
    api.moderatorAvailability.list(params),
  getModeratorAvailabilityByModerator: (moderatorId: string, approvedOnly?: boolean) =>
    api.moderatorAvailability.getByModerator(moderatorId, approvedOnly),
  createModeratorAvailability: (data: any) => api.moderatorAvailability.create(data),
  createModeratorAvailabilityBulk: (data: any[]) =>
    api.moderatorAvailability.bulkCreate(data),
  updateModeratorAvailability: (id: string, data: any) =>
    api.moderatorAvailability.update(id, data),
  deleteModeratorAvailability: (id: string) => api.moderatorAvailability.delete(id),
  checkModeratorAvailabilityDelete: (availabilityIds: string[]) =>
    api.moderatorAvailability.checkDelete(availabilityIds),
  deleteModeratorAvailabilityByModerator: (moderatorId: string) =>
    api.moderatorAvailability.deleteByModerator(moderatorId),
  approveModeratorAvailability: (id: string) => api.moderatorAvailability.approve(id),
  requestDeleteModeratorAvailability: (id: string) =>
    api.moderatorAvailability.requestDelete(id),
  rejectModeratorAvailability: (id: string) => api.moderatorAvailability.reject(id),

  listScheduleLocations: () => api.scheduleLocations.list(),
  getScheduleLocation: (id: string) => api.scheduleLocations.get(id),
  getScheduleLocationsByProject: (projectId: string) =>
    api.scheduleLocations.getByProject(projectId),
  createScheduleLocation: (data: any) => api.scheduleLocations.create(data),
  updateScheduleLocation: (id: string, data: any) =>
    api.scheduleLocations.update(id, data),
  deleteScheduleLocation: (id: string) => api.scheduleLocations.delete(id),
  toggleScheduleLocation: (id: string) => api.scheduleLocations.toggle(id),

  listSessionTypes: () => api.sessionTypes.list(),
  getSessionType: (id: string) => api.sessionTypes.get(id),
  listDetailedSessionTypes: () => api.sessionTypes.detailed(),
  listDetailedSessionTypesByProject: (projectId: string) =>
    api.sessionTypes.detailedByProject(projectId),
  listSessionTypesByProject: (projectId: string) =>
    api.sessionTypes.getByProject(projectId),
  createSessionType: (data: any) => api.sessionTypes.create(data),
  updateSessionType: (id: string, data: any) => api.sessionTypes.update(id, data),
  deleteSessionType: (id: string) => api.sessionTypes.delete(id),
  toggleSessionType: (id: string) => api.sessionTypes.toggle(id),
};
